import { Button, ButtonThemes } from '@src/components/button/Button.component'
import { SignupButton } from '@src/components/signup-button/SignupButton.component'
import { Breakpoints, useBreakpoint } from '@src/hooks/useBreakpoint'
import classNames from 'classnames'
import { DatoCmsFileField } from 'dato-cms-assets'
import { ReactNode } from 'react'
import { NcImage } from '../nc-image/NcImage.component'
import styles from './LastChance.module.scss'

export interface LastChanceProps {
  heading: string
  paragraph?: string
  isBackgroundImage: boolean
  limitWidth?: boolean
  image: DatoCmsFileField
  helperImage?: DatoCmsFileField
  ctaUrl?: string
  cta: string
  ctaTheme?: ButtonThemes
  signupButtonTheme?: ButtonThemes
  contentPlacement?: 'regular' | 'left'
  theme?: 'black' | 'purple' | 'roundedCorners'
}

export function LastChance({
  heading,
  cta,
  ctaUrl,
  paragraph = '',
  limitWidth = false,
  isBackgroundImage = false,
  image,
  helperImage,
  signupButtonTheme = 'digitalBlue',
  ctaTheme = 'digitalBlue',
  contentPlacement = 'regular',
  theme = 'purple',
}: LastChanceProps): ReactNode {
  const isMobile = useBreakpoint().breakpoint < Breakpoints.bpMedium
  const alignedLeft = contentPlacement === 'left'

  function getImageUrl(image: DatoCmsFileField): string | undefined {
    return image.responsiveImage.src || image.url || undefined
  }

  const imageUrl = isBackgroundImage
    ? getImageUrl(isMobile || !helperImage ? image : helperImage)
    : undefined

  const backgroundImageStyle = imageUrl
    ? {
        backgroundImage: `url(${imageUrl}`,
      }
    : {}

  const sectionClass = classNames(styles.LastChance, {
    [styles.LastChance___withBackground]: isBackgroundImage,
    [styles.LastChance___leftDesign]: alignedLeft,
    [styles.LastChance___withBackgroundLimited]: limitWidth,
    [styles.LastChance___roundedCorners]: theme === 'roundedCorners',
  })

  const contentClass = classNames({
    [styles.LastChance__content___left]: contentPlacement === 'left',
  })

  const headlineClass = classNames(styles.LastChance__headline, {
    [styles.LastChance__headline___left]: alignedLeft,
    [styles.LastChance__headline___black]: theme === 'black',
  })

  return (
    <section style={backgroundImageStyle} className={sectionClass}>
      <div className={contentClass}>
        <h2 className={headlineClass}>{heading}</h2>
        {paragraph && paragraph.length > 0 && (
          <div
            className={styles.LastChance__paragraph}
            dangerouslySetInnerHTML={{ __html: paragraph }}
          />
        )}
        <div className={styles.LastChance__signup}>
          {ctaUrl ? (
            <Button theme={ctaTheme} href={ctaUrl}>
              {cta}
            </Button>
          ) : (
            <SignupButton theme={signupButtonTheme} size="flexible">
              {cta}
            </SignupButton>
          )}
        </div>
        {!isBackgroundImage && <NcImage className={styles.LastChance__image} {...image} />}
      </div>
    </section>
  )
}
